import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, Plus, Check, Upload, Download, Paperclip, X, Eye, Trash2, Layers, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import ReportIndexTab from './ReportIndexTab';
import InformationForm from './InformationForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getH = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('crm_token')}` });
const getAuthH = () => ({ Authorization: `Bearer ${localStorage.getItem('crm_token')}` });

/** Status / Remark cascade options — same as Overview tables */
const STATUS_OPTIONS = ['INPROCESS', 'HOLD', 'CANCEL', 'DONE'];
const REMARK_BY_STATUS = {
  INPROCESS: ['Call Back later', 'Office visit Schedule', 'Office Visit Done', 'Project visit Schedule', 'Project Visit Done', 'Ask Project details'],
  HOLD:      ['High Rate', 'Instalment', 'Long Distance', 'NA Plot', 'No Development', 'No Money'],
  CANCEL:    ['CP working', 'Not interested'],
  DONE:      ['Booking', 'Booking Instalments', 'Kharedikhat Inprocess', 'Kharedikhat Done', '7/12 Inprocess', '7/12 Done', 'Possession'],
};
const STATUS_SELECT_CLS = {
  INPROCESS: 'bg-amber-50 text-amber-700',
  HOLD:      'bg-blue-50 text-blue-700',
  CANCEL:    'bg-red-50 text-red-700',
  DONE:      'bg-emerald-50 text-emerald-700',
  '':        'bg-white text-stone-500',
};

const INDEX_SUBTABS = [
  { slug: 'index', label: '1. Index' },
  { slug: 'targetindex', label: '2. Target Index' },
  { slug: 'counselinglist', label: '3. Counsling List' },
  { slug: 'dreamlist', label: '4. Dream List' },
  { slug: 'marketingsetup', label: '5. Marketing Set Up' },
];

const TOP10_SUBTABS = [
  { slug: 'top10list',      label: '1. Top 10 List' },
  { slug: 'top100lead',     label: '2. Top 100 Customer Lead List' },
  { slug: 'officevisit',    label: '3. Office Visiter List' },
  { slug: 'projectvisit',   label: '4. Project Visiter List' },
  { slug: 'booking',        label: '5. Booking' },
  { slug: 'confirmsale',    label: '6. Confirm Sale List' },
  { slug: 'commission',     label: '7. Commission Statement' },
  { slug: 'paymentdetails', label: '8. Payment Details' },
  { slug: 'dailywork',      label: '9. DM Daily Work' },
  { slug: 'bangkoklist',    label: '10. Bangkok Achievers' },
];

// Build helpers for merged cells
function buildMergeMaps(merges) {
  const origin = {};   // "r_c" -> { rowspan, colspan }
  const covered = {};  // "r_c" -> true (covered but not origin)
  (merges || []).forEach(([r, c, rs, cs]) => {
    origin[`${r}_${c}`] = { rowspan: rs, colspan: cs };
    for (let i = 0; i < rs; i++) {
      for (let j = 0; j < cs; j++) {
        if (i === 0 && j === 0) continue;
        covered[`${r + i}_${c + j}`] = true;
      }
    }
  });
  return { origin, covered };
}

const MySheetsTab = ({ user, empId = null, overviewContent = null, ownerName = null, onSheetSaved = null, onReturnToOverview = null }) => {
  const [activeMain, setActiveMain] = useState('overview');
  const [activeIndexSub, setActiveIndexSub] = useState(INDEX_SUBTABS[0].slug);
  const [activeTop10Sub, setActiveTop10Sub] = useState(TOP10_SUBTABS[0].slug);
  const [variant, setVariant] = useState(() => ((user?.role || '').toLowerCase() === 'cp' ? 'cp' : 'dm_adm'));
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('idle');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null); // null | array
  const [confirmsaleDocs, setConfirmsaleDocs] = useState({});  // keyed by rowKey
  const [docModal, setDocModal] = useState(null);              // { rowKey, srNo, customer }
  const [docUploading, setDocUploading] = useState(false);
  const docInputRef = useRef(null);
  const docUploadMeta = useRef(null); // { rowKey, docType }
  const saveTimer = useRef(null);
  const importInputRef = useRef(null);
  const bulkInputRef = useRef(null);
  // Always-current callback refs — updated on every render
  const onSheetSavedRef = useRef(onSheetSaved);
  const onReturnToOverviewRef = useRef(onReturnToOverview);
  useEffect(() => { onSheetSavedRef.current = onSheetSaved; });
  useEffect(() => { onReturnToOverviewRef.current = onReturnToOverview; });

  // Wrap setActiveMain so switching TO Overview always fires the refresh callback
  const handleSetActiveMain = (tab) => {
    if (tab === 'overview' && activeMain !== 'overview') {
      if (onReturnToOverviewRef.current) onReturnToOverviewRef.current();
    }
    setActiveMain(tab);
  };

  const isDmAdm = variant === 'dm_adm';
  const activeSlug = activeMain === 'index' ? activeIndexSub : (isDmAdm ? activeTop10Sub : 'top10');
  const canImport = activeMain === 'index' || (activeMain === 'top10' && isDmAdm);
  // Extra tabs (Index 1 & 2, Personal Info) are only shown for DM/ADM/CP viewing their OWN sheet
  const showExtraTabs = !empId && user && ['dm', 'adm', 'cp'].includes((user?.role || '').toLowerCase());

  const fetchSheet = useCallback(async (slug) => {
    setLoading(true);
    setSheet(null);
    try {
      const q = empId ? `?emp_id=${encodeURIComponent(empId)}` : '';
      const res = await fetch(`${API}/my-sheets/${slug}${q}`, { headers: getH() });
      const data = await res.json();
      setSheet(data);
      if (data?.variant) setVariant(data.variant);
      // Load doc attachments for confirm sale
      if (slug === 'confirmsale') {
        const docsRes = await fetch(`${API}/my-sheets/confirmsale/docs`, { headers: getAuthH() });
        if (docsRes.ok) {
          const docs = await docsRes.json();
          const grouped = {};
          docs.forEach((d) => { if (!grouped[d.rowKey]) grouped[d.rowKey] = []; grouped[d.rowKey].push(d); });
          setConfirmsaleDocs(grouped);
        }
      }
    } catch (e) {
      setSheet(null);
    }
    setLoading(false);
  }, [empId]);

  useEffect(() => {
    if (activeMain === 'overview' || activeMain === 'index12' || activeMain === 'personalinfo') return;
    fetchSheet(activeSlug);
  }, [activeMain, activeSlug, fetchSheet]);

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !docUploadMeta.current) return;
    const { rowKey, docType } = docUploadMeta.current;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('row_key', rowKey);
      fd.append('doc_type', docType);
      const res = await fetch(`${API}/my-sheets/confirmsale/docs`, { method: 'POST', headers: getAuthH(), body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const doc = await res.json();
      // Update confirmsaleDocs state
      setConfirmsaleDocs(prev => {
        const updated = { ...prev };
        if (!updated[rowKey]) updated[rowKey] = [];
        updated[rowKey] = [...updated[rowKey], doc];
        return updated;
      });
      // Optimistically write ✓ in the corresponding sheet columns
      const colMap = { doc712: 13, kharedikhat: 12 };
      const targetCol = colMap[docType];
      setSheet(prev => {
        if (!prev) return prev;
        const cells = prev.cells.map(row => row.slice());
        const rowIdx = cells.findIndex((row, i) =>
          (String(row[0] || '').trim() || String(i)) === rowKey
        );
        if (rowIdx >= prev.headerRows && rowIdx < cells.length) {
          // General Document column (11) — mark whenever any file is uploaded
          if (cells[rowIdx].length > 11) cells[rowIdx][11] = '✓';
          // Specific column (Kharedikhat=12, 7/12=13)
          if (targetCol !== undefined && cells[rowIdx].length > targetCol) {
            cells[rowIdx][targetCol] = '✓';
          }
          scheduleSave(cells);
        }
        return { ...prev, cells };
      });
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    }
    setDocUploading(false);
    e.target.value = '';
  };

  const handleDocDelete = async (docId, rowKey) => {
    try {
      await fetch(`${API}/my-sheets/confirmsale/docs/${docId}`, { method: 'DELETE', headers: getAuthH() });
      setConfirmsaleDocs(prev => {
        const updated = { ...prev };
        updated[rowKey] = (updated[rowKey] || []).filter(d => d.id !== docId);
        return updated;
      });
      toast.success('Document removed');
    } catch {
      toast.error('Delete failed');
    }
  };

  const scheduleSave = useCallback((cells) => {
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await fetch(`${API}/my-sheets/${activeSlug}`, {
          method: 'PUT', headers: getH(), body: JSON.stringify(empId ? { cells, emp_id: empId } : { cells }),
        });
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 1500);
        // Notify parent so KPIs + My Leads can refresh
        if (onSheetSavedRef.current) onSheetSavedRef.current(activeSlug);
      } catch (e) { setSaveState('idle'); }
    }, 800);
  }, [activeSlug, empId]);

  const editCell = (r, c, val) => {
    setSheet(prev => {
      const cells = prev.cells.map(row => row.slice());
      cells[r][c] = val;
      scheduleSave(cells);
      return { ...prev, cells };
    });
  };

  const addRow = () => {
    setSheet(prev => {
      const cells = prev.cells.map(row => row.slice());
      cells.push(Array(prev.ncols).fill(''));
      scheduleSave(cells);
      return { ...prev, cells };
    });
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (empId) fd.append('emp_id', empId);
      const res = await fetch(`${API}/my-sheets/${activeSlug}/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Import failed');
      toast.success(`Imported ${data.rowsImported} rows from "${data.matchedSheet}"`);
      fetchSheet(activeSlug);
      // Notify parent after import too
      if (onSheetSavedRef.current) onSheetSavedRef.current(activeSlug);
    } catch (err) {
      toast.error(err.message || 'Import failed');
    }
    setImporting(false);
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBulkImporting(true);
    setBulkResults(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (empId) fd.append('emp_id', empId);
      const res = await fetch(`${API}/my-sheets/bulk-import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Bulk import failed');
      setBulkResults(data.results || []);
      toast.success(`All sheets imported! ${data.results.length} tabs updated.`);
      // Refresh current sheet
      fetchSheet(activeSlug);
      if (onSheetSavedRef.current) onSheetSavedRef.current(activeSlug);
    } catch (err) {
      toast.error(err.message || 'Bulk import failed');
    }
    setBulkImporting(false);
  };

  const handleExport = async () => {    setExporting(true);
    try {
      const q = empId ? `?emp_id=${encodeURIComponent(empId)}` : '';
      const res = await fetch(`${API}/my-sheets/${activeSlug}/export${q}`, { headers: getH() });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeSlug}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    }
    setExporting(false);
  };

  const merged = sheet ? buildMergeMaps(sheet.merges) : { origin: {}, covered: {} };

  return (
    <div data-testid="my-sheets-tab" className="space-y-4">
      {/* Header + sub-tab switcher */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-stone-800">My Marketing Workbook</h2>
          <p className="text-sm text-stone-400">
            {empId
              ? <>Viewing <strong className="text-stone-600">{sheet?.ownerName || ownerName || empId}</strong> ({empId}) · {(sheet?.variant === 'cp' ? 'CP' : 'DM/ADM')} template — edits save automatically</>
              : <>{user?.firstName} {user?.lastName} · {(user?.role || '').toUpperCase()} template — edits save automatically</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => handleSetActiveMain('overview')}
            data-testid="sheet-tab-overview"
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeMain === 'overview' ? 'bg-[#1F3864] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
            Overview
          </button>
          <button onClick={() => handleSetActiveMain('index')}
            data-testid="sheet-tab-index"
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeMain === 'index' ? 'bg-[#1F3864] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
            1. Index
          </button>
          <button onClick={() => handleSetActiveMain('top10')}
            data-testid="sheet-tab-top10"
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeMain === 'top10' ? 'bg-[#1F3864] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
            2. Top 10
          </button>
          {showExtraTabs && (
            <>
              <button onClick={() => handleSetActiveMain('index12')}
                data-testid="sheet-tab-index12"
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeMain === 'index12' ? 'bg-amber-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                Index 1 &amp; 2
              </button>
              <button onClick={() => handleSetActiveMain('personalinfo')}
                data-testid="sheet-tab-personalinfo"
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeMain === 'personalinfo' ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                Personal Info
              </button>
            </>
          )}
        </div>
      </div>

      {/* Overview = rich dashboard-home content passed by the parent (stat cards, charts, real data) */}
      {activeMain === 'overview' && (overviewContent || <p className="text-sm text-stone-400 py-8 text-center">No overview content available.</p>)}

      {/* Index 1 & 2 = Department attendance checklists (ReportIndexTab) */}
      {activeMain === 'index12' && <ReportIndexTab user={user} isHR={false} />}

      {/* Personal Info = Information / profile form */}
      {activeMain === 'personalinfo' && <InformationForm />}

      {/* Sub-tab strip for Index */}
      {activeMain === 'index' && (
        <div className="flex items-center gap-2 flex-wrap border-b border-stone-100 pb-3" data-testid="index-subtabs">
          {INDEX_SUBTABS.map(t => (
            <button key={t.slug}
              onClick={() => setActiveIndexSub(t.slug)}
              data-testid={`sheet-subtab-${t.slug}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeIndexSub === t.slug ? 'bg-amber-600 text-white' : 'bg-stone-50 text-stone-500 hover:bg-stone-100 border border-stone-200'}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Sub-tab strip for Top 10 (DM/ADM only) */}
      {activeMain === 'top10' && isDmAdm && (
        <div className="flex items-center gap-2 flex-wrap border-b border-stone-100 pb-3" data-testid="top10-subtabs">
          {TOP10_SUBTABS.map(t => (
            <button key={t.slug}
              onClick={() => setActiveTop10Sub(t.slug)}
              data-testid={`sheet-subtab-${t.slug}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeTop10Sub === t.slug ? 'bg-amber-600 text-white' : 'bg-stone-50 text-stone-500 hover:bg-stone-100 border border-stone-200'}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Toolbar */}
      {activeMain !== 'overview' && activeMain !== 'index12' && activeMain !== 'personalinfo' && (
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={addRow} disabled={!sheet}
          data-testid="add-sheet-row-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
          <Plus className="w-4 h-4" /> Add Row
        </button>
        {canImport && (
          <>
            <input ref={importInputRef} type="file" accept=".xlsx,.xls" className="hidden"
              data-testid="import-sheet-file-input" onChange={handleImportFile} />
            <button onClick={() => importInputRef.current?.click()} disabled={importing}
              data-testid="import-sheet-btn"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import This Sheet
            </button>
          </>
        )}
        {/* Bulk Import: upload once, all tabs populated automatically */}
        {isDmAdm && (
          <>
            <input ref={bulkInputRef} type="file" accept=".xlsx,.xls" className="hidden"
              data-testid="bulk-import-file-input" onChange={handleBulkImport} />
            <button
              onClick={() => bulkInputRef.current?.click()}
              disabled={bulkImporting}
              data-testid="bulk-import-btn"
              title="Upload your Excel workbook once — all tabs (Top 10, Leads, Bookings, Commission, etc.) are imported automatically"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-[#1F3864] text-white hover:bg-[#2c4f8a] disabled:opacity-40">
              {bulkImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
              {bulkImporting ? 'Importing All…' : 'Import All Sheets'}
            </button>
          </>
        )}
        <div className="text-xs text-stone-400 flex items-center gap-1.5" data-testid="sheet-save-state">
          {saveState === 'saving' && <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>}
          {saveState === 'saved' && <><Check className="w-3.5 h-3.5 text-emerald-600" /> Saved</>}
        </div>
        {sheet && <span className="text-xs text-stone-400 ml-auto">{sheet.ncols} columns · scroll horizontally →</span>}
      </div>
      )}

      {/* Grid */}
      {activeMain !== 'overview' && activeMain !== 'index12' && activeMain !== 'personalinfo' && (
      <div className="border border-stone-200 rounded-xl overflow-auto bg-white" style={{ maxHeight: '68vh' }}>
        {loading ? (
          <div className="flex items-center justify-center py-20 text-stone-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading sheet…
          </div>
        ) : !sheet ? (
          <div className="py-20 text-center text-stone-400">Could not load sheet.</div>
        ) : (
          <table className="border-collapse text-xs" style={{ tableLayout: 'fixed' }}>
            <tbody>
              {(() => {
                const colHeaders = sheet.cells[sheet.headerRows - 1] || [];
                const statusColIdx = colHeaders.findIndex(h => String(h).toUpperCase().trim() === 'STATUS');
                return sheet.cells.map((row, r) => {
                const isHeader = r < sheet.headerRows;
                return (
                  <tr key={r}>
                    {row.map((val, c) => {
                      const key = `${r}_${c}`;
                      if (merged.covered[key]) return null;
                      const mo = merged.origin[key];
                      const span = mo ? { rowSpan: mo.rowspan, colSpan: mo.colspan } : {};
                      const colHeader = String(colHeaders[c] || '').toUpperCase().trim();
                      const isStatusCol = !isHeader && colHeader === 'STATUS';
                      const isRemarkCol = !isHeader && colHeader === 'REMARK';
                      // Width: STATUS=130, REMARK=170, SR NO=48, others=90
                      const colWidth = (c === 0 && !mo?.colspan) ? 48 : isStatusCol ? 130 : isRemarkCol ? 170 : 90;
                      if (isHeader) {
                        return (
                          <th key={c} {...span}
                            className="border border-stone-300 px-2 py-1.5 font-semibold text-white align-middle text-center whitespace-nowrap"
                            style={{ background: '#1F3864', minWidth: colWidth, maxWidth: c === 0 && !mo?.colspan ? 52 : undefined, position: r === 0 ? 'sticky' : undefined, top: r === 0 ? 0 : undefined, zIndex: r === 0 ? 2 : 1 }}>
                            {val}
                          </th>
                        );
                      }
                      // STATUS dropdown
                      if (isStatusCol) {
                        return (
                          <td key={c} {...span} className="border border-stone-200 p-0" style={{ minWidth: colWidth }}>
                            <select
                              value={val || ''}
                              onChange={(e) => editCell(r, c, e.target.value)}
                              data-testid={`cell-${r}-${c}`}
                              className={`w-full px-1 py-1.5 text-xs outline-none cursor-pointer border-0 ${STATUS_SELECT_CLS[val || '']}`}
                              style={{ minWidth: colWidth }}
                            >
                              <option value="">— Status —</option>
                              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                        );
                      }
                      // REMARK cascade dropdown
                      if (isRemarkCol) {
                        const rowStatus = statusColIdx >= 0 ? String(row[statusColIdx] || '') : '';
                        const stdOpts = REMARK_BY_STATUS[rowStatus] || [];
                        const curVal = val || '';
                        // If stored value isn't in standard options, show it as an extra option so data isn't lost
                        const showExtra = curVal && !stdOpts.includes(curVal);
                        return (
                          <td key={c} {...span} className="border border-stone-200 p-0" style={{ minWidth: colWidth }}>
                            <select
                              value={curVal}
                              onChange={(e) => editCell(r, c, e.target.value)}
                              disabled={!rowStatus}
                              data-testid={`cell-${r}-${c}`}
                              className="w-full px-1 py-1.5 text-xs outline-none cursor-pointer border-0 bg-white text-stone-600 disabled:opacity-40 disabled:cursor-not-allowed"
                              style={{ minWidth: colWidth }}
                            >
                              <option value="">— Remark —</option>
                              {showExtra && <option value={curVal}>{curVal}</option>}
                              {stdOpts.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </td>
                        );
                      }
                      return (
                        <td key={c} {...span} className="border border-stone-200 p-0" style={{ minWidth: colWidth, maxWidth: c === 0 ? 52 : undefined }}>
                          <input
                            value={val}
                            onChange={(e) => editCell(r, c, e.target.value)}
                            data-testid={`cell-${r}-${c}`}
                            className="w-full px-2 py-1.5 text-xs outline-none focus:bg-blue-50 focus:ring-1 focus:ring-blue-400 bg-transparent text-center"
                            style={c === 0 ? { textAlign: 'center' } : undefined}
                          />
                        </td>
                      );
                    })}
                    {/* Docs column — only for confirmsale sheet */}
                    {sheet.slug === 'confirmsale' && (() => {
                      const rowKey = String(row[0] || '').trim() || String(r);
                      const rowDocs = confirmsaleDocs[rowKey] || [];
                      const customerName = String(row[3] || '').trim();
                      if (isHeader) {
                        return (
                          <th className="border border-stone-300 px-2 py-1.5 font-semibold text-white align-middle text-center whitespace-nowrap"
                            style={{ background: '#1F3864', minWidth: 60, position: 'sticky', top: 0, zIndex: 2 }}>
                            Docs
                          </th>
                        );
                      }
                      return (
                        <td className="border border-stone-200 p-1 text-center" style={{ minWidth: 60 }}>
                          <button
                            onClick={() => setDocModal({ rowKey, srNo: row[0], customer: customerName })}
                            data-testid={`doc-btn-${rowKey}`}
                            className={`relative inline-flex items-center gap-1 px-1.5 py-1 rounded text-xs font-semibold transition-colors ${rowDocs.length > 0 ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}
                            title="Attach / view documents"
                          >
                            <Paperclip className="w-3.5 h-3.5" />
                            {rowDocs.length > 0 && <span className="text-[10px] font-bold">{rowDocs.length}</span>}
                          </button>
                        </td>
                      );
                    })()}
                  </tr>
                );
              });
              })()}
            </tbody>
          </table>
        )}
      </div>
      )}

      {/* Bulk Import Results Modal */}
      {bulkResults && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-[#1F3864] px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-white font-bold text-sm flex items-center gap-2">
                  <Layers className="w-4 h-4" /> Import All Sheets — Complete
                </p>
                <p className="text-blue-200 text-xs mt-0.5">{bulkResults.length} tabs imported from your workbook</p>
              </div>
              <button onClick={() => setBulkResults(null)} className="text-blue-200 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 max-h-96 overflow-y-auto space-y-1.5">
              {bulkResults.map((r) => (
                <div key={r.slug} className="flex items-center justify-between gap-3 bg-stone-50 px-3 py-2 rounded-lg border border-stone-100">
                  <div className="flex items-center gap-2 min-w-0">
                    {r.rowsImported > 0
                      ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                      : <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-stone-700 truncate">{r.slug}</p>
                      <p className="text-[10px] text-stone-400 truncate">from "{r.matchedSheet}"</p>
                    </div>
                  </div>
                  <span className={`text-xs font-bold shrink-0 px-2 py-0.5 rounded-full ${r.rowsImported > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>
                    {r.rowsImported} rows
                  </span>
                </div>
              ))}
            </div>
            <div className="px-5 pb-4">
              <button
                onClick={() => setBulkResults(null)}
                data-testid="bulk-results-close-btn"
                className="w-full py-2 bg-[#1F3864] text-white rounded-xl text-sm font-semibold hover:bg-[#2c4f8a] transition-colors">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Doc Upload Modal */}
      {docModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-[#1F3864] px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-white font-bold text-sm flex items-center gap-2"><Paperclip className="w-4 h-4" /> Documents</p>
                <p className="text-blue-200 text-xs mt-0.5">SR {docModal.srNo} — {docModal.customer || 'Row'}</p>
              </div>
              <button onClick={() => setDocModal(null)} className="text-blue-200 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {/* Upload Buttons */}
              <input ref={docInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleDocUpload} />
              <div className="grid grid-cols-2 gap-2">
                {[{ type: 'doc712', label: '7/12 Document' }, { type: 'kharedikhat', label: 'Kharedikhat Doc' }].map(({ type, label }) => (
                  <button
                    key={type}
                    disabled={docUploading}
                    onClick={() => { docUploadMeta.current = { rowKey: docModal.rowKey, docType: type }; docInputRef.current?.click(); }}
                    data-testid={`upload-${type}-btn`}
                    className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-stone-100 text-stone-700 text-xs font-semibold hover:bg-stone-200 disabled:opacity-60 border border-stone-200"
                  >
                    <Upload className="w-3.5 h-3.5 text-blue-600" /> {label}
                  </button>
                ))}
              </div>
              {/* Existing docs */}
              {(confirmsaleDocs[docModal.rowKey] || []).length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Uploaded Files</p>
                  {(confirmsaleDocs[docModal.rowKey] || []).map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between gap-2 bg-stone-50 px-3 py-2 rounded-lg border border-stone-100">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-stone-700 truncate">{doc.filename}</p>
                        <p className="text-[10px] text-stone-400 uppercase">{doc.docType === 'doc712' ? '7/12' : doc.docType === 'kharedikhat' ? 'Kharedikhat' : 'Other'}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.fileUrl}`} target="_blank" rel="noreferrer"
                          className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100">
                          <Eye className="w-3.5 h-3.5" />
                        </a>
                        <button onClick={() => handleDocDelete(doc.id, docModal.rowKey)}
                          className="p-1.5 rounded bg-red-50 text-red-500 hover:bg-red-100">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-stone-400 text-center py-2">No documents uploaded yet.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySheetsTab;
